import type { DownloadEvent, Update } from '@tauri-apps/plugin-updater'
import { useCallback, useEffect } from 'react'
import { createLogger } from '@/lib/logger'
import { isTauri, saveUpdateRestartPath } from '@/lib/utils/tauri'
import { useUpdateStore } from '@/store/update.store'
import { toast } from 'sonner'

const logger = createLogger('hooks/useAutoUpdater')

/**
 * Auto-Updater Hook
 * Listens for update events from the Tauri backend and manages update state
 * Uses Zustand store for persistent update notifications
 */

interface UpdateAvailablePayload {
  version: string
  body: string
  currentVersion?: string
}

interface UpdateNotAvailablePayload {
  currentVersion: string
}

// Track if the current check was manually triggered (for toast behavior)
let isManualCheck = false

let inFlightPrefetch: Promise<Update | null> | null = null

interface AutoUpdaterOptions {
  enableListeners?: boolean
}

interface DownloadState {
  downloadedBytes: number
  contentLength: number
}

const calculateDownloadProgress = ({
  downloadedBytes,
  contentLength,
}: DownloadState) => {
  if (contentLength <= 0) {
    return downloadedBytes
  }

  return Math.min(100, Math.round((downloadedBytes / contentLength) * 100))
}

const shouldUseDownloadedUpdate = (args: {
  existingVersion: string | null
  downloadedVersion: string
  updateDownloaded: boolean
}) => {
  return (
    args.updateDownloaded && args.existingVersion === args.downloadedVersion
  )
}

export function useAutoUpdater(options: AutoUpdaterOptions = {}) {
  const { enableListeners = true } = options

  const {
    setUpdateAvailable,
    setUpdateDownloaded,
    setChecking,
    setInstalling,
    setDownloadProgress,
  } = useUpdateStore()

  const predownloadUpdate = useCallback(async (): Promise<Update | null> => {
    if (!isTauri()) {
      return null
    }

    if (inFlightPrefetch) {
      return inFlightPrefetch
    }

    const predownload = (async () => {
      let downloadedBytes = 0
      let contentLength = 0

      try {
        const { check } = await import('@tauri-apps/plugin-updater')
        const update = await check()

        if (!update) {
          setUpdateAvailable(false)
          setUpdateDownloaded(false)
          setDownloadProgress(0)
          return null
        }

        const availableVersion = update.version
        const { updateInfo, updateDownloaded } = useUpdateStore.getState()
        const hasCurrentDownload = shouldUseDownloadedUpdate({
          existingVersion: updateInfo?.version || null,
          downloadedVersion: availableVersion,
          updateDownloaded,
        })

        setUpdateAvailable(true, {
          version: availableVersion,
          body: update.body || '',
        })

        if (hasCurrentDownload) {
          setDownloadProgress(100)
          return update
        }

        setUpdateDownloaded(false)
        setDownloadProgress(0)

        await update.download((event: DownloadEvent) => {
          if (event.event === 'Started') {
            contentLength = event.data.contentLength ?? 0
            downloadedBytes = 0
            setDownloadProgress(0)
            return
          }

          if (event.event === 'Progress') {
            downloadedBytes += event.data.chunkLength
            setDownloadProgress(
              calculateDownloadProgress({ downloadedBytes, contentLength }),
            )
            return
          }

          if (event.event === 'Finished') {
            setDownloadProgress(100)
          }
        })

        setUpdateDownloaded(true)
        return update
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        if (message.includes('does not have any endpoints')) {
          logger.debug('[AutoUpdater] Updater not configured (local build)')
          return null
        }
        logger.error('[AutoUpdater] Failed to pre-download update:', error)
        return null
      }
    })()

    inFlightPrefetch = predownload

    try {
      return await predownload
    } finally {
      inFlightPrefetch = null
    }
  }, [setDownloadProgress, setUpdateAvailable, setUpdateDownloaded])

  // Listen for update events from Tauri backend and perform a startup check
  useEffect(() => {
    if (!isTauri() || !enableListeners) {
      return
    }

    let unlisten: (() => void) | undefined

    const setupListeners = async () => {
      try {
        const { listen } = await import('@tauri-apps/api/event')

        const unlistenChecking = await listen('checking-for-updates', () => {
          isManualCheck = true
          setChecking(true)
          toast.loading('Checking for updates...', { id: 'update-check' })
        })

        const unlistenAvailable = await listen<UpdateAvailablePayload>(
          'update-available',
          () => {
            setChecking(false)

            toast.success('A new version is available. Downloading now.', {
              id: 'update-check',
              description: 'The update is being downloaded in the background.',
              duration: 5000,
            })

            isManualCheck = false

            // Keep check toast responsive: start pre-download in the background.
            void predownloadUpdate()
          },
        )

        const unlistenNotAvailable = await listen<UpdateNotAvailablePayload>(
          'update-not-available',
          event => {
            setChecking(false)
            setUpdateAvailable(false)
            setUpdateDownloaded(false)

            if (isManualCheck) {
              toast.success("You're up to date!", {
                id: 'update-check',
                description: `Version ${event.payload?.currentVersion || 'unknown'} is the latest.`,
                duration: 4000,
              })
            }

            isManualCheck = false
          },
        )

        const unlistenError = await listen<string>('update-error', event => {
          logger.error('[AutoUpdater] Update error:', event.payload)
          setChecking(false)

          if (isManualCheck) {
            toast.error('Update Check Failed', {
              id: 'update-check',
              description: event.payload,
            })
          }

          isManualCheck = false
        })

        unlisten = () => {
          unlistenChecking()
          unlistenAvailable()
          unlistenNotAvailable()
          unlistenError()
        }

        await predownloadUpdate()
      } catch (error) {
        logger.error('[AutoUpdater] Failed to set up update listeners:', error)
      }
    }

    setupListeners()

    return () => {
      if (unlisten) {
        unlisten()
      }
    }
  }, [
    enableListeners,
    predownloadUpdate,
    setChecking,
    setUpdateAvailable,
    setUpdateDownloaded,
  ])

  // Manual check for updates (can be called from settings page)
  const checkForUpdates = useCallback(async () => {
    if (!isTauri()) return

    setChecking(true)
    toast.loading('Checking for updates...', { id: 'update-check' })

    try {
      const update = await predownloadUpdate()

      if (update) {
        setUpdateAvailable(true, {
          version: update.version,
          body: update.body || '',
        })

        toast.success(`Update v${update.version} available!`, {
          id: 'update-check',
          description: useUpdateStore.getState().updateDownloaded
            ? 'Update is ready to install.'
            : 'Update is being downloaded in the background.',
        })
      } else {
        const { getVersion } = await import('@tauri-apps/api/app')
        const currentVersion = await getVersion()

        setUpdateAvailable(false)
        setUpdateDownloaded(false)
        toast.success("You're up to date!", {
          id: 'update-check',
          description: `Version ${currentVersion} is the latest.`,
        })
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'

      if (errorMessage.includes('does not have any endpoints')) {
        logger.debug('[AutoUpdater] Updater not configured (local build)')
        toast.dismiss('update-check')
      } else {
        logger.error('[AutoUpdater] Update check failed:', error)
        toast.error('Update Check Failed', {
          id: 'update-check',
          description: errorMessage,
        })
      }
    } finally {
      setChecking(false)
    }
  }, [setChecking, setUpdateAvailable, setUpdateDownloaded, predownloadUpdate])

  const installUpdate = useCallback(async () => {
    if (!isTauri()) return

    const restartPath =
      window.location.pathname + window.location.search + window.location.hash

    setInstalling(true)
    setDownloadProgress(0)

    try {
      const { check } = await import('@tauri-apps/plugin-updater')
      const { relaunch } = await import('@tauri-apps/plugin-process')

      const update = await check()
      if (!update) {
        throw new Error('No update available.')
      }

      try {
        await update.install()
      } catch (error) {
        logger.warn(
          '[AutoUpdater] Install without re-download failed, falling back to downloadAndInstall',
          error,
        )
        await update.downloadAndInstall((event: DownloadEvent) => {
          if (event.event === 'Started') {
            setDownloadProgress(0)
            return
          }

          if (event.event === 'Progress') {
            setDownloadProgress(
              event.data.chunkLength > 0
                ? Math.min(100, event.data.chunkLength)
                : 100,
            )
          }

          if (event.event === 'Finished') {
            setDownloadProgress(100)
          }
        })
      }

      setUpdateDownloaded(false)
      setUpdateAvailable(false)

      toast.success('Update installed! Restarting...', { id: 'update-install' })

      // Persist only after a successful install so the next launch consumes it
      // specifically as an updater return path (including global routes like /settings).
      await saveUpdateRestartPath(restartPath)

      await new Promise(resolve => setTimeout(resolve, 1500))
      await relaunch()
    } catch (error) {
      logger.error('[AutoUpdater] Update installation failed:', error)
      const errorMessage =
        error instanceof Error ? error.message : String(error)

      let description = errorMessage
      if (
        errorMessage.includes('relaunch') ||
        errorMessage.includes('process')
      ) {
        description =
          'Failed to restart the app. Please close and reopen manually.'
      } else if (
        errorMessage.includes('permission') ||
        errorMessage.includes('denied')
      ) {
        description = 'Permission denied. Try running as administrator.'
      } else if (
        errorMessage.includes('network') ||
        errorMessage.includes('download')
      ) {
        description = 'Download failed. Check your internet connection.'
      }

      toast.error('Update Failed', {
        id: 'update-install',
        description,
      })
    } finally {
      setInstalling(false)
    }
  }, [
    setDownloadProgress,
    setInstalling,
    setUpdateAvailable,
    setUpdateDownloaded,
  ])

  return {
    checkForUpdates,
    installUpdate,
  }
}

export default useAutoUpdater
